import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Alert, Snackbar } from "@mui/material";

// global snackbar.
//
// Replaces per-page `<Snackbar>` state that existed in every editor. The
// provider mounts once at App.jsx, exposes `useSnackbar()`, and queues
// messages so an error and a success don't clobber each other.
//
// Intentionally narrow surface: callers pass a severity + message. Advanced
// one-offs (custom undo snackbars, pending-timer snackbars) keep their own
// `<Snackbar>` so the queue doesn't fight with them.

const SnackbarContext = createContext({
  show: () => {},
  close: () => {},
});

export function useSnackbar() {
  return useContext(SnackbarContext);
}

export default function SnackbarProvider({
  children,
  autoHideDuration = 3200,
  anchorOrigin = { vertical: "top", horizontal: "center" },
}) {
  const [current, setCurrent] = useState(null);
  const [open, setOpen] = useState(false);
  const queueRef = useRef([]);

  const processQueue = useCallback(() => {
    const next = queueRef.current.shift();
    if (next) {
      setCurrent(next);
      setOpen(true);
    }
  }, []);

  const show = useCallback(
    (input) => {
      const message =
        typeof input === "string" ? input : input?.message ?? "";
      if (!message) return;

      const entry = {
        key: Date.now() + Math.random(),
        severity: typeof input === "object" ? input.severity || "info" : "info",
        message,
        action: typeof input === "object" ? input.action : null,
        duration:
          typeof input === "object" && Number.isFinite(input.duration)
            ? input.duration
            : autoHideDuration,
      };

      if (open) {
        queueRef.current.push(entry);
        setOpen(false); // triggers transition; exited handler drains the queue
      } else {
        setCurrent(entry);
        setOpen(true);
      }
    },
    [autoHideDuration, open],
  );

  const close = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  }, []);

  const handleExited = useCallback(() => {
    setCurrent(null);
    processQueue();
  }, [processQueue]);

  const value = useMemo(
    () => ({ show, close: () => setOpen(false) }),
    [show],
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.key}
        open={open}
        onClose={close}
        autoHideDuration={current?.duration ?? autoHideDuration}
        anchorOrigin={anchorOrigin}
        TransitionProps={{ onExited: handleExited }}
      >
        {current ? (
          <Alert
            variant="filled"
            onClose={close}
            severity={current.severity}
            action={current.action || undefined}
            sx={{ minWidth: 240, fontWeight: 500 }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  );
}
